import React, { Component } from 'react';
import classNames from 'classnames';
import get from 'lodash/get';
import reject from 'lodash/reject';
import groupBy from 'lodash/groupBy';
import union from 'lodash/union';
import { produce } from 'immer';
import styles from './PaymentMethodChecklist.css';
import akulakuLogoUrl from '../images/akulaku-logo.png';
import alipayLogoUrl from '../images/alipay-logo.png';
import danaLogoUrl from '../images/dana-logo.png';
import gopayLogoUrl from '../images/gopay-logo.png';
import kredivoLogoUrl from '../images/kredivo-logo.png';
import linkajaLogoUrl from '../images/linkaja-logo.png';
import ovoLogoUrl from '../images/ovo-logo.png';
import wechatpayLogoUrl from '../images/wechatpay-logo.png';
import shopeePayLogoUrl from '../images/shopeepay-logo.png';

const logos = {
  akulaku: akulakuLogoUrl,
  alipay: alipayLogoUrl,
  dana: danaLogoUrl,
  gopay: gopayLogoUrl,
  kredivo: kredivoLogoUrl,
  tcash: linkajaLogoUrl,
  ovo: ovoLogoUrl,
  wechatpay: wechatpayLogoUrl,
  shopeepay: shopeePayLogoUrl,
};

function PaymentMethodRow(props) {
  const { method, isSelected, disabled, onChange } = props;
  const {
    id,
    name,
    code,
    category,
    isIosEnabled = true,
    isAndroidEnabled = true,
  } = method;

  let imageUrl;
  if (category.code === 'e-wallet') {
    imageUrl = logos[code];
  }

  return (
    <li className={styles.listItem}>
      <label className={styles.checkboxLabel}>
        <input
          type="checkbox"
          onChange={e => onChange(id, e.target.checked)}
          checked={isSelected}
          disabled={disabled}
        />
        {imageUrl && <img src={imageUrl} />}
        {name}
      </label>
      {isAndroidEnabled && !isIosEnabled && (
        <div className={classNames('alert alert-warning', styles.alertBox)}>
          <i
            className={classNames(
              'glyphicon glyphicon-info-sign',
              styles.infoIcon
            )}
          />
          <div>{name} is currently only available on Android Tablet</div>
        </div>
      )}
    </li>
  );
}

class PaymentMethodChecklist extends Component {
  constructor(props) {
    super(props);

    this.state = {
      expandedSections: new Set(),
    };
  }

  toggleSectionExpansion = categoryCode => {
    const { expandedSections } = this.state;

    const newValue = produce(expandedSections, draft => {
      if (draft.has(categoryCode)) {
        draft.delete(categoryCode);
      } else {
        draft.add(categoryCode);
      }
    });

    this.setState({
      expandedSections: newValue,
    });
  };

  handlePaymentMethodChange = (methodId, toInclude) => {
    const { selectedIds, onChange } = this.props;
    let newSelections;

    if (toInclude) {
      newSelections = [...selectedIds, methodId];
    } else {
      newSelections = reject(selectedIds, id => id === methodId);
    }

    onChange(newSelections);
  };

  handleCategoryCheckboxChange = (categoryCode, toInclude) => {
    const { paymentMethods, selectedIds, onChange } = this.props;
    const methodIdsInCategory = paymentMethods
      .filter(paymentMethod => paymentMethod.category.code === categoryCode)
      .map(({ id }) => id);
    let newSelection;

    if (toInclude) {
      newSelection = union(selectedIds, methodIdsInCategory);
    } else {
      newSelection = reject(selectedIds, id =>
        methodIdsInCategory.includes(id)
      );

      if (newSelection.length === 0) {
        const firstSelectedMethod = methodIdsInCategory.find(id =>
          selectedIds.includes(id)
        );
        newSelection = [firstSelectedMethod];
      }
    }

    onChange(newSelection);
  };

  render() {
    const {
      paymentMethods,
      selectedIds,
      disabled,
      paymentMethodCategories,
    } = this.props;
    const methodsByCategory = groupBy(paymentMethods, pm => pm.category.code);
    const listItems = [];

    const isSelected = new Set(selectedIds);
    const hasOnlyOnePaymentMethod = selectedIds.length === 1;

    for (const category of paymentMethodCategories) {
      const { code: categoryCode, name: categoryName } = category;
      const methods = get(methodsByCategory, categoryCode, []);

      // If there is nothing in this category, hide it
      if (methods.length === 0) {
        continue;
      }

      // If there is only one payment method in a category,
      // that method itself is a category
      if (methods.length === 1) {
        listItems.push(
          <PaymentMethodRow
            method={methods[0]}
            key={categoryCode}
            isSelected={isSelected.has(methods[0].id)}
            onChange={this.handlePaymentMethodChange}
            disabled={
              disabled ||
              (hasOnlyOnePaymentMethod && isSelected.has(methods[0].id))
            }
          />
        );
      } else {
        // Otherwise, a category has a list of nested payment methods,
        // each can be individually turned on/off
        const { expandedSections } = this.state;
        const showNestedItems = expandedSections.has(categoryCode);
        const selectedCount = methods.filter(({ id }) => isSelected.has(id))
          .length;
        const isAllSelected = selectedCount === methods.length;
        const isPartiallySelected = !isAllSelected && selectedCount > 0;

        const categoryHeader = (
          <li
            className={classNames(styles.listItem, styles.categoryHeader)}
            key={categoryCode}
          >
            {/*
              Checkbox intentionally put outside of label to give section
              toggling more spaces. Batch toggling selection is dangerous so
              it is limited to the checkbox only.
            */}
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={e =>
                this.handleCategoryCheckboxChange(
                  categoryCode,
                  e.target.checked
                )
              }
              disabled={disabled}
              ref={el => {
                // Set `indeterminate` state when it is partially selected
                if (el) {
                  el.indeterminate = isPartiallySelected;
                }
              }}
            />
            <div
              className={styles.categoryToggle}
              onClick={() => this.toggleSectionExpansion(categoryCode)}
            >
              <label className={styles.checkboxLabel}>{categoryName}</label>
              <i
                className={classNames('fa', {
                  'fa-caret-up': showNestedItems,
                  'fa-caret-down': !showNestedItems,
                })}
              />
            </div>
          </li>
        );
        listItems.push(categoryHeader);

        if (showNestedItems) {
          const nestedItems = (
            <ul
              className={styles.nestedFormRow}
              key={`${categoryCode}-children`}
            >
              {methods.map(method => (
                <PaymentMethodRow
                  method={method}
                  key={method.id}
                  isSelected={isSelected.has(method.id)}
                  onChange={this.handlePaymentMethodChange}
                  disabled={
                    disabled ||
                    (hasOnlyOnePaymentMethod && isSelected.has(method.id))
                  }
                />
              ))}
            </ul>
          );
          listItems.push(nestedItems);
        }
      }
    }

    return <ul>{listItems}</ul>;
  }
}

PaymentMethodChecklist.defaultProps = {
  paymentMethods: [],
  selectedIds: [],
  onChange: () => {},
};

export default PaymentMethodChecklist;
